import { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, CalendarDays, CalendarPlus, Clock, MapPin, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useSettings } from "@/hooks/SettingsContext";
import { pinDate, downloadIcs } from "@/lib/calendar";
import { trackEventRegistration } from "@/lib/analytics";

export { pinDate };

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const CHURCH_LOCATION = "Victory Chapel - 3180 CR 220 STE 6-7, Middleburg, FL 32068";
const SERVICES_START = "2027-01-01"; // weekly services begin with the January 2027 launch

function toKey(y, m, d) {
  return `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

export default function EventCalendar({ events }) {
  const { settings } = useSettings();
  const today = new Date();
  const todayKey = toKey(today.getFullYear(), today.getMonth(), today.getDate());

  // Real events with a full date (YYYY-MM-DD) pinned per day
  const byDay = useMemo(() => {
    const map = {};
    events.forEach((e) => {
      const k = pinDate(e.date);
      if (k) (map[k] = map[k] || []).push({ ...e, real: true });
    });
    return map;
  }, [events]);

  // Weekly services (from launch month onward), generated per day
  const recurringFor = (key) => {
    if (key < SERVICES_START) return [];
    const dow = new Date(`${key}T12:00:00`).getDay();
    const out = [];
    if (dow === 0 && settings?.sunday_time) {
      out.push({
        id: `rec-sun-${key}`, date: key, title: "Sunday Service", time: settings.sunday_time,
        location: CHURCH_LOCATION, recurring: true,
        description: "Our weekly Sunday gathering - worship, Bible-centered preaching, and genuine community. Everyone welcome.",
      });
    }
    if (dow === 3 && settings?.wednesday_time) {
      out.push({
        id: `rec-wed-${key}`, date: key, title: "Wednesday Service", time: settings.wednesday_time,
        location: CHURCH_LOCATION, recurring: true,
        description: "Midweek service - prayer, Bible study, and connection to carry you through the week.",
      });
    }
    return out;
  };

  const eventsOn = (key) => [...(byDay[key] || []), ...recurringFor(key)];

  const firstEventMonth = useMemo(() => {
    const keys = Object.keys(byDay).sort();
    if (!keys.length) return null;
    const [y, m] = keys[0].split("-").map(Number);
    return { y, m: m - 1 };
  }, [byDay]);

  const [view, setView] = useState(() => {
    if (firstEventMonth && firstEventMonth.y * 12 + firstEventMonth.m > today.getFullYear() * 12 + today.getMonth()) {
      return firstEventMonth;
    }
    return { y: today.getFullYear(), m: today.getMonth() };
  });
  const [selected, setSelected] = useState(null);

  const shift = (n) => {
    setSelected(null);
    setView((v) => {
      const d = new Date(v.y, v.m + n, 1);
      return { y: d.getFullYear(), m: d.getMonth() };
    });
  };

  const addToCal = (e) => {
    if (downloadIcs(e)) toast.success("Calendar file downloaded - open it to save the event");
  };

  const firstDow = new Date(view.y, view.m, 1).getDay();
  const daysInMonth = new Date(view.y, view.m + 1, 0).getDate();
  const cells = [...Array(firstDow).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];

  const monthHasEvents = cells.some((d) => d && eventsOn(toKey(view.y, view.m, d)).length);
  const selectedEvents = selected ? eventsOn(selected) : [];

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,26rem)_1fr]" data-testid="events-calendar">
      <div className="rounded-3xl border border-navy/10 bg-white p-5 shadow-soft sm:p-6">
        <div className="flex items-center justify-between">
          <button type="button" onClick={() => shift(-1)} aria-label="Previous month" data-testid="cal-prev"
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-navy/15 text-navy transition-colors hover:bg-navy hover:text-white">
            <ChevronLeft size={18} />
          </button>
          <h2 className="font-display text-lg font-800 tracking-tight text-navy" data-testid="cal-month-label">
            {MONTHS[view.m]} {view.y}
          </h2>
          <button type="button" onClick={() => shift(1)} aria-label="Next month" data-testid="cal-next"
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-navy/15 text-navy transition-colors hover:bg-navy hover:text-white">
            <ChevronRight size={18} />
          </button>
        </div>

        <div className="mt-4 grid grid-cols-7 gap-1 text-center">
          {DOW.map((d) => (
            <span key={d} className="pb-1 text-[11px] font-700 uppercase tracking-widest text-slate-400">{d}</span>
          ))}
          {cells.map((d, i) => {
            if (!d) return <span key={`x${i}`} />;
            const key = toKey(view.y, view.m, d);
            const dayEvents = eventsOn(key);
            const has = dayEvents.length > 0;
            const isToday = key === todayKey;
            const isSel = key === selected;
            return (
              <button
                key={key}
                type="button"
                onClick={() => has && setSelected(key)}
                disabled={!has}
                data-testid={`cal-day-${key}`}
                aria-label={`${MONTHS[view.m]} ${d}${has ? ` - ${dayEvents.length} event${dayEvents.length > 1 ? "s" : ""}` : ""}`}
                className={`relative flex aspect-square items-center justify-center rounded-xl text-sm font-600 transition-colors ${
                  isSel
                    ? "bg-navy text-white"
                    : has
                      ? "bg-victory/10 font-800 text-victory hover:bg-victory hover:text-white"
                      : "text-slate-500"
                } ${isToday && !isSel ? "ring-1 ring-inset ring-victory" : ""}`}
              >
                {d}
                {has && !isSel && <span className="absolute bottom-1 h-1 w-1 rounded-full bg-victory" aria-hidden="true" />}
              </button>
            );
          })}
        </div>
        <p className="mt-3 text-center text-xs text-slate-400">Days marked in red have events - tap one for details.</p>
      </div>

      <div className="rounded-3xl border border-navy/10 bg-white p-6 shadow-soft sm:p-8" data-testid="cal-details-panel">
        {selected && selectedEvents.length ? (
          <div>
            <p className="font-display text-xs font-700 uppercase tracking-[0.3em] text-victory">
              {new Date(`${selected}T12:00:00`).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })}
            </p>
            <ul className="mt-4 space-y-5">
              {selectedEvents.map((e) => (
                <li key={e.id} className="border-l-2 border-victory pl-4" data-testid={`cal-event-${e.id}`}>
                  <div className="flex items-center gap-2.5">
                    <h3 className="font-display text-xl font-800 tracking-tight text-navy">{e.title}</h3>
                    {e.recurring && (
                      <span className="shrink-0 rounded-full bg-navy px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-white">Weekly</span>
                    )}
                  </div>
                  <ul className="mt-2 space-y-1.5 text-sm text-slate-600">
                    {e.time && <li className="flex items-center gap-2"><Clock size={14} className="text-victory" aria-hidden="true" /> {e.time}</li>}
                    {e.location && <li className="flex items-center gap-2"><MapPin size={14} className="text-victory" aria-hidden="true" /> {e.location}</li>}
                  </ul>
                  {e.description && <p className="mt-2 text-sm leading-relaxed text-slate-600">{e.description}</p>}
                  <div className="mt-3 flex flex-wrap gap-2.5">
                    {e.registration_url && (
                      <a href={e.registration_url} target="_blank" rel="noopener noreferrer" onClick={() => trackEventRegistration(e.title)}
                         data-testid={`cal-register-${e.id}`}
                         className="inline-flex items-center gap-1.5 rounded-full bg-victory px-5 py-2 font-display text-xs font-700 text-white transition-colors hover:bg-victory-dark">
                        REGISTER <ArrowRight size={13} aria-hidden="true" />
                      </a>
                    )}
                    <button type="button" onClick={() => addToCal(e)} data-testid={`cal-addcal-${e.id}`}
                            className="inline-flex items-center gap-1.5 rounded-full border border-navy/20 px-5 py-2 font-display text-xs font-700 text-navy transition-colors hover:bg-navy hover:text-white">
                      <CalendarPlus size={13} aria-hidden="true" /> ADD TO CALENDAR
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <div className="flex h-full flex-col items-center justify-center py-8 text-center">
            <CalendarDays size={36} className="text-victory/40" aria-hidden="true" />
            <p className="mt-4 font-display text-lg font-700 text-navy">
              {monthHasEvents ? "Pick a highlighted day" : `No events in ${MONTHS[view.m]} ${view.y}`}
            </p>
            <p className="mt-2 max-w-sm text-sm text-slate-500">
              {monthHasEvents
                ? "Tap any red-marked day to see what's happening."
                : "Use the arrows to browse other months, or check the full list below. Weekly services begin January 2027."}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
